import {
    FC,
    ChangeEvent,
    useMemo,
    Dispatch,
    SetStateAction,
    useState,
    useCallback,
    useEffect,
    useRef
} from 'react'
import useIsMobile from '@lib/hooks/useIsMobile'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import { WishlistManager } from '../Hooks/WishlistActionTypes'
import dynamic from 'next/dynamic'
import { capitalizeFirstLetter } from '@lib/utils/capitalizeFirstLetter'
import { DeleteWishlist } from '../DeleteWishlist'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { useWishlist } from '../Hooks/use-wishlist'
import { Cross, Success } from '@components/icons'
import { Wishlist } from '@pylot-data/pylotschema'
import { WishlistVisibilityEnum } from '@pylot-data/enums/WishlistVisibilityEnum'
import { useUpdateWishlist } from '../Hooks/use-update-wishlist'

import s from '@pagestyles/Wishlist.module.scss'
import cn from 'classnames'

const Modal = dynamic(() => import('@corratech/pylot-ui/src/Modal'))

export type Props = {
    wishlist: Wishlist
    wishlistManager: WishlistManager
    setShowEditForm: Dispatch<SetStateAction<boolean>>
}

const DEFAULT_NAME = 'Lista de deseos'
const DEFAULT_NAME_REPLACEMENT = 'Favoritos guardados'

export const WishlistToolbar: FC<Props> = ({
    wishlist,
    wishlistManager,
    setShowEditForm
}) => {
    const { t } = useTranslation(['wishlist'])
    const router = useRouter()
    const { wishlistState, addSelected, resetSelected } = wishlistManager
    const [shareModal, setShareModal] = useState<boolean>(false)
    const [preparingShareLink, setPreparingShareLink] = useState(false)
    const [successMessage, setSuccessMessage] = useState(false)
    const config = useStoreConfig()
    const { openToast } = useUI()
    const { updateWishlist } = useUpdateWishlist()
    const autoOpenedShareFromQuery = useRef(false)

    const isMobile = useIsMobile()
    const { wishlists, mutate: mutateWishlists } = useWishlist({
        revalidateOnFocus: false,
        revalidateOnMount: false
    })
    const listIndex = wishlists?.findIndex((list) => list?.id === wishlist?.id)
    const baseUrl =
        typeof window !== 'undefined'
            ? window.location.origin
            : config.base.url.baseUrl!

    const effectiveWishlist = useMemo(
        () => wishlists?.find((list) => list?.id === wishlist?.id) ?? wishlist,
        [wishlists, wishlist]
    )
    const sharingCode = effectiveWishlist?.sharing_code?.trim() ?? ''
    const linkToShare = sharingCode ? `${baseUrl}/wishlist/${sharingCode}` : ''

    const openShareModal = useCallback(async () => {
        if (!wishlist?.id) {
            return
        }
        const current =
            wishlists?.find((list) => list?.id === wishlist.id) ?? wishlist
        const needsPublic =
            current?.visibility !== WishlistVisibilityEnum.Public

        setShareModal(true)

        if (!needsPublic) {
            return
        }

        setPreparingShareLink(true)
        try {
            const { errors } = await updateWishlist({
                wishlistId: wishlist.id,
                visibility: WishlistVisibilityEnum.Public
            })
            if (errors?.length) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger,
                    false
                )
                setShareModal(false)
            }
        } catch {
            openToast(
                t('wishlist|Something went wrong. Please try again'),
                ToastType.Danger,
                false
            )
            setShareModal(false)
        } finally {
            setPreparingShareLink(false)
        }
    }, [wishlist, wishlists, updateWishlist, openToast, t])

    useEffect(() => {
        autoOpenedShareFromQuery.current = false
    }, [wishlist?.id])

    useEffect(() => {
        if (!router.isReady || router.query.share !== '1' || !wishlist?.id) {
            return
        }
        if (autoOpenedShareFromQuery.current) {
            return
        }
        autoOpenedShareFromQuery.current = true
        void (async () => {
            await openShareModal()
            const nextQuery = { ...router.query }
            delete nextQuery.share
            void router.replace(
                { pathname: router.pathname, query: nextQuery },
                undefined,
                { shallow: true }
            )
        })()
    }, [
        router.isReady,
        router.query.share,
        wishlist?.id,
        openShareModal,
        router
    ])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const ids = wishlist?.items_v2?.items?.map((item) => item?.id) as []
            addSelected(ids)
        } else {
            resetSelected()
        }
    }

    const copyToClipboard = (text: string) => {
        if (!text) {
            openToast(
                t('wishlist|Something went wrong. Please try again'),
                ToastType.Danger,
                false
            )
            return
        }
        navigator.clipboard.writeText(text).then(
            () => {
                // setSuccessMessage(true)
                setShareModal(false)
                openToast(
                    t('wishlist|Link copied to clipboard'),
                    ToastType.Success,
                    false
                )
            },
            () => {
                setShareModal(false)
                openToast(
                    t('wishlist|Link copied to clipboard'),
                    ToastType.Danger,
                    false
                )
            }
        )
    }

    const canSelectAll = useMemo(() => {
        return wishlist?.items_v2?.items?.every((item) =>
            wishlistState?.selected?.includes(String(item?.id))
        )
    }, [wishlist, wishlistState])

    const listHasNoItems = !wishlist?.items_v2?.items.length

    const successMessageSection = successMessage == true && (
        <div className={s['sharemodal-success-container']}>
            <div className={s['sharemodal-success-info']}>
                <Success />
                {t('Tu comentario fue enviado')}
            </div>
            <button onClick={() => setSuccessMessage(false)}>
                <Cross />
            </button>
        </div>
    )

    const shareWishlist = useCallback(
        (explicitLink?: string) => {
            const url = explicitLink ?? linkToShare
            if (!url) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger,
                    false
                )
                return
            }
            const message = t('wishlist|Check out my favorites on Casaideas')
            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
                `${message} ${url}`
            )}`

            if (navigator.share) {
                navigator.share({
                    title: wishlist?.name!,
                    text: message,
                    url
                })
            } else {
                window.open(whatsappUrl, '_blank')
            }

            setShareModal(false)
        },
        [linkToShare, wishlist?.name, t, openToast]
    )

    const resolveShareLinkFromWishlists = useCallback(
        (lists: typeof wishlists) => {
            const effective =
                lists?.find((list) => list?.id === wishlist?.id) ?? wishlist
            const code = effective?.sharing_code?.trim() ?? ''
            const origin =
                typeof window !== 'undefined'
                    ? window.location.origin
                    : config.base.url.baseUrl!
            return code ? `${origin}/wishlist/${code}` : ''
        },
        [wishlist, config.base.url.baseUrl]
    )

    const handleMobileShareDirect = useCallback(async () => {
        if (!wishlist?.id) {
            return
        }
        const current =
            wishlists?.find((list) => list?.id === wishlist.id) ?? wishlist
        const needsPublic =
            current?.visibility !== WishlistVisibilityEnum.Public

        if (needsPublic) {
            try {
                const { errors } = await updateWishlist({
                    wishlistId: wishlist.id,
                    visibility: WishlistVisibilityEnum.Public
                })
                if (errors?.length) {
                    openToast(
                        t('wishlist|Something went wrong. Please try again'),
                        ToastType.Danger,
                        false
                    )
                    return
                }
            } catch {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger,
                    false
                )
                return
            }
        }

        let resolvedLink = linkToShare
        if (needsPublic && mutateWishlists) {
            try {
                const refreshed = (await mutateWishlists()) as
                    | {
                          data?: {
                              data?: {
                                  customer?: { wishlists?: typeof wishlists }
                              }
                          }
                      }
                    | undefined
                const lists = refreshed?.data?.data?.customer?.wishlists
                resolvedLink = resolveShareLinkFromWishlists(lists)
            } catch {
                resolvedLink = ''
            }
        }

        if (!resolvedLink) {
            openToast(
                t('wishlist|Something went wrong. Please try again'),
                ToastType.Danger,
                false
            )
            return
        }

        shareWishlist(resolvedLink)
    }, [
        wishlist,
        wishlists,
        linkToShare,
        updateWishlist,
        openToast,
        t,
        mutateWishlists,
        resolveShareLinkFromWishlists,
        shareWishlist
    ])

    const handleShareListClick = () => {
        if (isMobile) {
            void handleMobileShareDirect()
        } else {
            void openShareModal()
        }
    }

    const isDefaultListName = wishlist?.name! === DEFAULT_NAME

    const titleRender = isDefaultListName
        ? DEFAULT_NAME_REPLACEMENT
        : capitalizeFirstLetter(wishlist?.name!)

    return (
        <div className={s['wishlist-top-toolbar']}>
            <div className={s['wishlist-toolbar-actions-container']}>
                <div className={s['wishlist-toolbar-actions']}>
                    <div className={s['wishlist-toolbar-name']}>
                        {titleRender}
                    </div>
                    <div className={s['wishlist-toolbar-action']}>
                        <button onClick={() => setShowEditForm(true)}>
                            {t('wishlist|Edit name')}
                        </button>
                        {listIndex !== 0 && (
                            <>
                                <div>&nbsp;{' | '}&nbsp;</div>
                                <DeleteWishlist
                                    wishlist={wishlist}
                                    listIndex={listIndex!}
                                />
                            </>
                        )}

                        {/* <div>{t('wishlist|Delete')}</div> */}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleShareListClick}
                    className={s['wishlist-toolbar-sharebutton']}
                >
                    {t('wishlist|Share list')}
                </button>
            </div>
            {!listHasNoItems && (
                <div className={s['checkbox-container']}>
                    <label className="label" htmlFor="select_all">
                        <input
                            type="checkbox"
                            className={cn(s['form-checkbox'], 'form-checkbox')}
                            id="select_all"
                            onChange={handleChange}
                            checked={canSelectAll}
                        />
                        {t('wishlist|Select All')}
                    </label>
                </div>
            )}
            {shareModal && (
                <Modal
                    onClose={() => {
                        setShareModal(false)
                        setPreparingShareLink(false)
                    }}
                    open={shareModal}
                    className="wishlist-sharemodal"
                    closeOnOverlayClick
                >
                    {successMessageSection}
                    <div className={s['sharemodal-container']}>
                        <h2 className={s['sharemodal-title']}>
                            {t('wishlist|Share favorites')}
                        </h2>
                        <p className={s['sharemodal-subtitle']}>
                            {t('wishlist|Share this link')}
                        </p>
                        {preparingShareLink && (
                            <p className={s['sharemodal-subtitle']}>
                                {t('wishlist|Preparing share link')}
                            </p>
                        )}
                        <div className={s['sharemodal-actions']}>
                            <button
                                type="button"
                                className={s['sharemodal-outline-button']}
                                disabled={!linkToShare || preparingShareLink}
                                onClick={() => shareWishlist()}
                            >
                                {t('wishlist|Share whatsapp')}
                            </button>
                            <button
                                type="button"
                                className={s['sharemodal-outline-button']}
                                disabled={!linkToShare || preparingShareLink}
                                onClick={() => copyToClipboard(linkToShare)}
                            >
                                {t('wishlist|Copy link')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    )
}
